import React from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { i18n } from "../../../translate/i18n";

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: 4,
  fontSize: 9,
  tableLayout: "fixed",
};

const cellStyle = {
  border: "1px solid #ddd",
  padding: "4px 6px",
  verticalAlign: "top",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  WebkitPrintColorAdjust: "exact",
  printColorAdjust: "exact",
};

const headerCellStyle = {
  ...cellStyle,
  background: "#f0f3f9",
  fontWeight: 700,
  verticalAlign: "middle",
};

const chipContainerStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 4,
  margin: "0 0 4px 0",
};

const chipStyle = {
  display: "inline-block",
  padding: "1px 6px",
  borderRadius: 999,
  border: "1px solid #9bb3d9",
  background: "#eef3fb",
  color: "#1d3b66",
  fontSize: 10,
  fontWeight: 600,
  lineHeight: 1.4,
  WebkitPrintColorAdjust: "exact",
  printColorAdjust: "exact",
};

const observacoesRowCellStyle = {
  ...cellStyle,
  whiteSpace: "pre-wrap",
  lineHeight: 1.35,
};

const columnWidths = {
  dataContato: { width: "15%" },
  cliente: { width: "20%" },
  meioContato: { width: "15%" },
  observacoes: { width: "50%" },
};

// Motivo normalizado (valor do backend) → chave do rótulo exibido (traduzido na renderização).
const motivoLabelKeys = {
  FOLLOW_UP: "usageReport.reasons.followUp",
  REUNIAO: "usageReport.reasons.meetings",
  "REUNIÃO": "usageReport.reasons.meetings",
  LIGACAO: "usageReport.reasons.calls",
  "LIGAÇÃO": "usageReport.reasons.calls",
  VISITA: "usageReport.reasons.visits",
  EMAIL: "usageReport.reasons.emails",
  ORCAMENTO: "usageReport.reasons.quotes",
  "ORÇAMENTO": "usageReport.reasons.quotes",
  NEGOCIACAO: "usageReport.reasons.negotiations",
  "NEGOCIAÇÃO": "usageReport.reasons.negotiations",
  FECHAMENTO: "usageReport.reasons.closings",
  POS_VENDA: "usageReport.reasons.afterSales",
  RECLAMACAO: "usageReport.reasons.complaints",
  "RECLAMAÇÃO": "usageReport.reasons.complaints",
  INDICACAO: "usageReport.reasons.referrals",
  "INDICAÇÃO": "usageReport.reasons.referrals",
  OUTRO: "usageReport.reasons.others",
};

const normalizeMotivo = (motivo) => {
  if (!motivo) return "OUTRO";
  const normalized = motivo
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/\s+/g, "_");
  return normalized;
};

const buildUserSummary = (items = []) => {
  const counters = {};

  items.forEach((item) => {
    if (!Array.isArray(item.motivos) || item.motivos.length === 0) {
      counters.OUTRO = (counters.OUTRO || 0) + 1;
      return;
    }

    item.motivos.forEach((motivo) => {
      const key = normalizeMotivo(motivo);
      counters[key] = (counters[key] || 0) + 1;
    });
  });

  return Object.entries(counters)
    .sort((a, b) => b[1] - a[1])
    .map(([key, count]) => ({
      key,
      count,
      label: motivoLabelKeys[key] ? i18n.t(motivoLabelKeys[key]) : key,
    }));
};

const formatDateTime = (date) => {
  if (!date) return "-";
  try {
    return format(new Date(date), "dd/MM/yyyy HH:mm", { locale: ptBR });
  } catch {
    return "-";
  }
};

const UsageReportDetailedPrintTemplate = React.forwardRef(({ details = [], periodLabel = "" }, ref) => {
  const generatedAt = format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR });

  const groupedByUser = details.reduce((acc, item) => {
    const userKey = item.userName || i18n.t("usageReport.print.unknownUser");
    if (!acc[userKey]) acc[userKey] = [];
    acc[userKey].push(item);
    return acc;
  }, {});

  const orderedUsers = Object.keys(groupedByUser).sort((a, b) => a.localeCompare(b));

  return (
    <div ref={ref} style={{ padding: "4px 8px", color: "#111", fontFamily: "Arial, sans-serif" }}>
      <h2 style={{ margin: 0, fontSize: 16 }}>{i18n.t("usageReport.print.detailedTitle")}</h2>
      <p style={{ margin: "2px 0 0 0", fontSize: 11 }}>{i18n.t("usageReport.periodLabel", { period: periodLabel || "-" })}</p>
      <p style={{ margin: "1px 0 0 0", fontSize: 11 }}>{i18n.t("usageReport.generatedAt", { date: generatedAt })}</p>
      <p style={{ margin: "1px 0 6px 0", fontSize: 11 }}>{i18n.t("usageReport.print.totalRecords", { total: details.length })}</p>

      {orderedUsers.length === 0 && (
        <p style={{ marginTop: 16, fontSize: 12 }}>{i18n.t("usageReport.print.noRecordsForFilters")}</p>
      )}

      {orderedUsers.map((userName, index) => (
        <React.Fragment key={userName}>
          {index > 0 && <div style={{ pageBreakBefore: "always", breakBefore: "page" }} />}
          <h3 style={{ margin: "0 0 2px 0", fontSize: 14, lineHeight: 1.2 }}>
            {userName} ({groupedByUser[userName].length})
          </h3>
          {buildUserSummary(groupedByUser[userName]).length === 0 ? (
            <p style={{ margin: "0 0 4px 0", fontSize: 11 }}>{i18n.t("usageReport.print.noTypeSummary")}</p>
          ) : (
            <div style={chipContainerStyle}>
              {buildUserSummary(groupedByUser[userName]).map((item) => (
                <span key={`${userName}-${item.key}`} style={chipStyle}>
                  {item.count} {item.label}
                </span>
              ))}
            </div>
          )}
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...headerCellStyle, ...columnWidths.dataContato }}>{i18n.t("usageReport.details.contactDate")}</th>
                <th style={{ ...headerCellStyle, ...columnWidths.cliente }}>{i18n.t("usageReport.details.client")}</th>
                <th style={{ ...headerCellStyle, ...columnWidths.meioContato }}>{i18n.t("usageReport.details.contactMethod")}</th>
                <th style={{ ...headerCellStyle, ...columnWidths.observacoes }}>{i18n.t("common.notes")}</th>
              </tr>
            </thead>
            <tbody>
              {groupedByUser[userName].map((item, rowIndex) => {
                const isStriped = rowIndex % 2 === 0;
                const recordBackground = isStriped ? "#f3f3f3" : "#ffffff";

                return (
                  <tr key={item.id}>
                    <td style={{ ...cellStyle, backgroundColor: recordBackground }}>{formatDateTime(item.data_contato)}</td>
                    <td style={{ ...cellStyle, backgroundColor: recordBackground }}>{item.clienteNome || "-"}</td>
                    <td style={{ ...cellStyle, backgroundColor: recordBackground }}>{item.meio_contato || "-"}</td>
                    <td style={{ ...observacoesRowCellStyle, backgroundColor: recordBackground }}>{item.observacoes || "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </React.Fragment>
      ))}
    </div>
  );
});

UsageReportDetailedPrintTemplate.displayName = "UsageReportDetailedPrintTemplate";

export default UsageReportDetailedPrintTemplate;
